import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Coach mark for the dashboard detail pages: the member names open a photo when
 * tapped, which is not otherwise discoverable. Every detail page renders it, but
 * it only ever greets the user once per login — the first dashboard page they
 * open after signing in shows it, and tapping anywhere (or pressing Escape)
 * removes it until the next login.
 */
const SEEN_KEY = "dashboardPhotoTapHintSeenFor";

// Second paragraph of the coach mark. Empty until the final refresh wording is
// supplied; the note is skipped entirely while it is blank.
const REFRESH_NOTE = "";

// The stored flag is tied to the current login rather than to the browser
// session: every sign-in writes a fresh token, so a new token means the hint has
// not been shown for this login yet.
const getLoginId = () => {
  try {
    const token = window.localStorage.getItem("token");
    if (!token || token === "null" || token === "undefined") return null;
    return token;
  } catch {
    return null;
  }
};

// Module-level mirror of the stored flag: storage throws in private browsing on
// some mobile browsers, so the in-memory copy keeps the once-only behaviour
// working even when storage is unavailable.
let seenForLogin = null;

const hasBeenSeen = () => {
  const loginId = getLoginId();
  if (loginId && seenForLogin === loginId) return true;
  try {
    return window.localStorage.getItem(SEEN_KEY) === loginId;
  } catch {
    return false;
  }
};

const markAsSeen = () => {
  const loginId = getLoginId();
  seenForLogin = loginId;
  try {
    window.localStorage.setItem(SEEN_KEY, loginId ?? "");
  } catch {
    /* storage unavailable — the module-level flag still holds for this login */
  }
};

export default function DashboardPhotoTapHint({
  heading = "IMPORTANT MESSAGE!",
  message = "In each of the dashboard sections, names can be tapped to view individual photos.",
  note = REFRESH_NOTE,
}) {
  // Read once on mount so a hint already dismissed on another page never flashes.
  const [visible, setVisible] = useState(() => !hasBeenSeen());

  const dismiss = useCallback(() => {
    markAsSeen();
    setVisible(false);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" || event.key === "Enter" || event.key === " ") {
        dismiss();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [visible, dismiss]);

  if (!visible) return null;

  // Rendered into the body: a `position: fixed` overlay resolves against any
  // transformed ancestor instead of the viewport, which is what pulled the card
  // off centre inside the page layout.
  return createPortal(
    <div
      className="dashboard-photo-tap-hint-overlay"
      role="presentation"
      onClick={dismiss}
    >
      <div className="dashboard-photo-tap-hint" role="status" aria-live="polite">
        <span className="dashboard-photo-tap-hint-icon" aria-hidden="true">
          📷
        </span>
        <p className="dashboard-photo-tap-hint-message">
          {heading ? (
            <span className="dashboard-photo-tap-hint-heading">{heading}</span>
          ) : null}
          {message}
        </p>
        {note ? <p className="dashboard-photo-tap-hint-note">{note}</p> : null}
        <span className="dashboard-photo-tap-hint-action">Tap anywhere to close</span>
      </div>
    </div>,
    document.body
  );
}
